"use client";

import { useState, useCallback, useMemo } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type { SlideImage } from "yet-another-react-lightbox";
import { absoluteCmsUrl } from "../../components/base-image/BaseImage";

function toSlide(media: any): SlideImage {
  const lg = media.formats?.large?.url;
  const md = media.formats?.medium?.url;
  const sm = media.formats?.small?.url;
  const src = absoluteCmsUrl(media.url || lg || md || sm);
  const srcSet = [
    media.url && { src: absoluteCmsUrl(media.url), width: media.width },
    media.formats?.large?.url && {
      src: absoluteCmsUrl(media.formats.large.url),
      width: media.formats.large.width,
    },
    media.formats?.medium?.url && {
      src: absoluteCmsUrl(media.formats.medium.url),
      width: media.formats.medium.width,
    },
    media.formats?.small?.url && {
      src: absoluteCmsUrl(media.formats.small.url),
      width: media.formats.small.width,
    },
  ].filter(Boolean) as { src: string; width: number }[];
  return {
    src,
    alt: media.alternativeText ?? media.caption ?? media.name,
    width: media.width,
    height: media.height,
    srcSet,
  } as SlideImage;
}

interface GalleryLightboxProps {
  images: any[];
}

export default function GalleryLightbox({ images }: GalleryLightboxProps) {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const slides = useMemo(() => {
    return images.map((img: any) => toSlide(img));
  }, [images]);

  const handleOpen = useCallback((index: number) => {
    setCurrentIndex(index);
    setOpen(true);
  }, []);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-10 px-6">
        {images.map((image: any, index: number) => (
          <button
            key={image.id || index}
            type="button"
            className="md:aspect-[4/3] sm:aspect-square overflow-hidden cursor-pointer relative group"
            onClick={() => handleOpen(index)}
            aria-label={`Open gallery image ${index + 1}`}
          >
            <img
              src={absoluteCmsUrl(image.url)}
              alt={image.alternativeText || `Gallery image ${index + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity duration-300 z-10" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
              <div className="text-center text-white flex items-center justify-center gap-2">
                <div className="w-7 h-7 mx-auto border border-white flex items-center justify-center">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
                <p className="uppercase font-medium">Open Gallery</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        index={currentIndex}
      />
    </>
  );
}
